import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import {
  Alert,
  Badge,
  Breadcrumbs,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconClipboardHeart,
  IconFileText,
  IconUser,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loading } from '../../components/Loading';
import { InfoRow } from '../clients/components';
import {
  getPatientOfficialName,
  getPatientPhone,
  getPatientEmail,
} from '../clients/utils/clientProfileFhir';

function formatDate(date: string | undefined): string {
  if (!date) return '--';
  try {
    return new Date(date).toLocaleDateString('en-US');
  } catch {
    return date;
  }
}

function formatAddress(patient: Patient): string {
  const address = patient.address?.[0];
  if (!address) return '--';

  const parts = [
    address.line?.join(', '),
    address.city,
    address.state,
    address.postalCode,
  ].filter(Boolean);

  return parts.join(', ') || '--';
}

/**
 * Patient detail page
 * Shows comprehensive patient information
 */
export function PatientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const medplum = useMedplum();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setError('No patient ID provided');
      setLoading(false);
      return;
    }

    medplum
      .readResource('Patient', id)
      .then((result) => {
        setPatient(result);
      })
      .catch(() => {
        setError('Failed to load patient details');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [medplum, id]);

  if (loading) {
    return <Loading />;
  }

  if (error || !patient) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={20} />} title="Error" color="red">
          {error || 'Patient not found'}
        </Alert>
      </Container>
    );
  }

  const breadcrumbItems = [
    { title: 'Patients', href: '/dashboard/patients' },
    { title: getPatientOfficialName(patient), href: '' },
  ].map((item, index) =>
    item.href ? (
      <Text key={index} component={Link} to={item.href} c="gold.7" size="sm">
        {item.title}
      </Text>
    ) : (
      <Text key={index} size="sm">
        {item.title}
      </Text>
    )
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>

        <Group justify="space-between">
          <div>
            <Title order={2}>{getPatientOfficialName(patient)}</Title>
            <Group gap="xs" mt="xs">
              <Badge color={patient.active === false ? 'gray' : 'green'} variant="light">
                {patient.active === false ? 'Inactive' : 'Active'}
              </Badge>
              {patient.gender && (
                <Badge color="blue" variant="light">
                  {patient.gender}
                </Badge>
              )}
            </Group>
          </div>
        </Group>

        <Tabs defaultValue="overview">
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconUser size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab value="encounters" leftSection={<IconClipboardHeart size={16} />}>
              Encounters
            </Tabs.Tab>
            <Tabs.Tab value="documents" leftSection={<IconFileText size={16} />}>
              Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder>
                  <Title order={5} mb="md">
                    Personal Information
                  </Title>
                  <Stack gap={0}>
                    <InfoRow label="Date of Birth" value={formatDate(patient.birthDate)} />
                    <InfoRow label="Gender" value={patient.gender || '--'} />
                    <InfoRow label="Marital Status" value={patient.maritalStatus?.text || '--'} />
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card withBorder>
                  <Title order={5} mb="md">
                    Contact Information
                  </Title>
                  <Stack gap={0}>
                    <InfoRow label="Phone" value={getPatientPhone(patient) || '--'} />
                    <InfoRow label="Email" value={getPatientEmail(patient) || '--'} />
                    <InfoRow label="Address" value={formatAddress(patient)} />
                  </Stack>
                </Card>
              </Grid.Col>

              <Grid.Col span={12}>
                <Card withBorder>
                  <Title order={5} mb="md">
                    Identifiers
                  </Title>
                  {patient.identifier && patient.identifier.length > 0 ? (
                    <Stack gap="xs">
                      {patient.identifier.map((id, index) => (
                        <Group key={index} gap="xs">
                          <Text size="sm" c="dimmed">
                            {id.system || 'ID'}:
                          </Text>
                          <Text size="sm" fw={500}>
                            {id.value}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  ) : (
                    <Text c="dimmed" size="sm">
                      No identifiers recorded
                    </Text>
                  )}
                </Card>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="encounters" pt="md">
            <Card withBorder>
              <Text c="dimmed">
                Encounter history will be displayed here once connected to Medplum.
              </Text>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="md">
            <Card withBorder>
              <Text c="dimmed">
                Clinical documents will be displayed here once connected to Medplum.
              </Text>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}
